import React, { useMemo } from "react";
import Button from "./Button";

export default function Pagination({
  page = 1,
  pageSize = 10,
  totalItems = 0,
  onChangePage,
  onChangePageSize,
  pageSizeOptions = [10, 20, 50, 100],
  className = "",
  compact = false,
}) {
  const totalPages = Math.max(
    1,
    Math.ceil((totalItems || 0) / (pageSize || 1))
  );
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIndex = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endIndex =
    totalItems === 0 ? 0 : Math.min(safePage * pageSize, totalItems);

  const goTo = (nextPage) => {
    const nextSafePage = Math.min(Math.max(1, nextPage), totalPages);
    if (nextSafePage !== safePage) onChangePage?.(nextSafePage);
  };

  const pages = useMemo(
    () => getPages(safePage, totalPages),
    [safePage, totalPages]
  );

  return (
    <div className={`w-full flex justify-center ${className}`}>
      <div className="w-full max-w-fit">
        <div className="flex flex-col items-center gap-3 rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3 w-full">
            <div className="flex items-center justify-center gap-3 w-full sm:w-auto">
              <div className="text-sm text-gray-600 text-center">
                {totalItems > 0 ? (
                  <span>
                    <span className="font-semibold text-primary">
                      {startIndex}
                    </span>{" "}
                    –{" "}
                    <span className="font-semibold text-primary">
                      {endIndex}
                    </span>{" "}
                    sur{" "}
                    <span className="font-semibold text-primary">
                      {totalItems}
                    </span>
                  </span>
                ) : (
                  <span></span>
                )}
              </div>

              {!!onChangePageSize && !compact && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Par page</span>
                  <select
                    value={pageSize}
                    onChange={(e) =>
                      onChangePageSize(parseInt(e.target.value, 10))
                    }
                    className="h-10 rounded-full border border-gray-200 bg-white px-3 text-sm text-primary focus:outline-none focus:ring-0"
                  >
                    {pageSizeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
              <Button
                as="button"
                type="button"
                color="secondary"
                size="sm"
                onClick={() => goTo(1)}
                disabled={safePage === 1}
                className="min-w-[44px]"
              >
                «
              </Button>

              <Button
                as="button"
                type="button"
                color="secondary"
                size="sm"
                onClick={() => goTo(safePage - 1)}
                disabled={safePage === 1}
                className="min-w-[44px]"
              >
                ‹
              </Button>

              <div className="flex items-center justify-center gap-1">
                {pages.map((p, idx) => {
                  if (p === "…") {
                    return (
                      <div
                        key={`dots-${idx}`}
                        className="h-10 px-2 flex items-center justify-center text-gray-500 select-none"
                      >
                        …
                      </div>
                    );
                  }

                  const isActive = p === safePage;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => goTo(p)}
                      className={`h-10 min-w-[44px] px-3 rounded-full transition text-sm font-semibold ${
                        isActive
                          ? "bg-[#fff3af] text-[#3a3a3a]"
                          : "bg-white text-primary hover:bg-gray-50"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>

              <Button
                as="button"
                type="button"
                color="secondary"
                size="sm"
                onClick={() => goTo(safePage + 1)}
                disabled={safePage === totalPages}
                className="min-w-[44px]"
              >
                ›
              </Button>

              <Button
                as="button"
                type="button"
                color="secondary"
                size="sm"
                onClick={() => goTo(totalPages)}
                disabled={safePage === totalPages}
                className="min-w-[44px]"
              >
                »
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getPages(current, total) {
  if (total <= 7) return range(1, total);

  const pages = [];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);

  pages.push(1);
  if (left > 2) pages.push("…");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push("…");
  pages.push(total);

  return pages;
}

function range(start, end) {
  const arr = [];
  for (let i = start; i <= end; i++) arr.push(i);
  return arr;
}
